// UseFormMutations.js

import { useCallback } from "react";
import { setPhrase, updateControl, clone } from "./Helpers";

/**
 * Centralizes all "write" operations against workingForm.
 * No side-effects beyond setWorkingForm.
 */
export function useFormMutations(setWorkingForm) {
  const handleChangePhrase = useCallback(
    (dictKey, value, lng = "en", type = "formDictionary") => {
      setWorkingForm((prev) => setPhrase(prev, lng, dictKey, value, type));
    },
    [setWorkingForm]
  );

  const handleChangeAttributes = useCallback(
    (pageIndex, controlIndex, patch) => {
      setWorkingForm((prev) =>
        updateControl(prev, pageIndex, controlIndex, (ctrl) => {
          ctrl.attributes = { ...(ctrl.attributes || {}), ...(patch || {}) };
        })
      );
    },
    [setWorkingForm]
  );

  const handleSetAttributes = useCallback(
    (pageIndex, controlIndex, nextAttributes) => {
      setWorkingForm((prev) =>
        updateControl(prev, pageIndex, controlIndex, (ctrl) => {
          ctrl.attributes =
            nextAttributes && typeof nextAttributes === "object" ? nextAttributes : {};
        })
      );
    },
    [setWorkingForm]
  );

  const handleSetControlType = useCallback(
    (pageIndex, controlIndex, nextType) => {
      const normalized = String(nextType ?? "").trim().toUpperCase();
      if (!normalized) return;
      setWorkingForm((prev) =>
        updateControl(prev, pageIndex, controlIndex, (ctrl) => {
          ctrl.type = normalized;
        })
      );
    },
    [setWorkingForm]
  );

  const handleRenamePageTitle = useCallback(
    (pageIndex, nextTitle) => {
      setWorkingForm((prev) => {
        const next = clone(prev);
        const page = next.pages?.[pageIndex];
        if (page) page.title = nextTitle ?? "";
        return next;
      });
    },
    [setWorkingForm]
  );

  const handleRenameControlKey = useCallback(
    (pageIndex, controlIndex, nextKey) => {
      setWorkingForm((prev) => {
        const next = clone(prev);
        const page = next.pages?.[pageIndex];
        if (page && Array.isArray(page.controls) && page.controls[controlIndex]) {
          page.controls[controlIndex] = {
            ...page.controls[controlIndex],
            key: nextKey ?? "",
          };
        }
        return next;
      });
    },
    [setWorkingForm]
  );

  return {
    handleChangePhrase,
    handleChangeAttributes,
    handleSetAttributes,
    handleSetControlType,
    handleRenamePageTitle,
    handleRenameControlKey,
  };
}
